import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Plus, Edit, Trash2, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_PERMISSIONS = [
  { id: 'users.view', label: 'View Users', category: 'Users' },
  { id: 'users.edit', label: 'Edit Users', category: 'Users' },
  { id: 'users.manage', label: 'Manage Users', category: 'Users' },
  { id: 'roles.manage', label: 'Manage Roles', category: 'Roles' },
  { id: 'points.manage', label: 'Manage Points', category: 'Points' },
  { id: 'apps.manage', label: 'Manage Apps', category: 'Apps' },
  { id: 'events.view', label: 'View Events', category: 'Events' },
  { id: 'events.create', label: 'Create Events', category: 'Events' },
  { id: 'self.view', label: 'View Own Profile', category: 'Self' },
  { id: 'self.edit', label: 'Edit Own Profile', category: 'Self' },
];

export default function RolesPage() {
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [roleLevel, setRoleLevel] = useState(10);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/roles`, { withCredentials: true });
      setRoles(res.data);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingRole(null);
    setRoleName('');
    setRoleLevel(10);
    setSelectedPermissions([]);
    setDialogOpen(true);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleLevel(role.level);
    setSelectedPermissions(role.permissions || []);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      if (editingRole) {
        await axios.patch(`${API}/roles/${editingRole.role_id}`, {
          name: roleName,
          level: roleLevel,
          permissions: selectedPermissions
        }, { withCredentials: true });
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/roles`, {
          name: roleName,
          level: roleLevel,
          permissions: selectedPermissions
        }, { withCredentials: true });
        toast.success('Role created');
      }
      setDialogOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDelete = async (roleId) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await axios.delete(`${API}/roles/${roleId}`, { withCredentials: true });
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(p => p !== permissionId)
        : [...prev, permissionId]
    );
  };

  const groupedPermissions = ALL_PERMISSIONS.reduce((acc, perm) => {
    if (!acc[perm.category]) acc[perm.category] = [];
    acc[perm.category].push(perm);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <Header title="Roles" subtitle="Manage user roles and permissions" />
      
      <div className="p-6 space-y-6">
        {/* Header Actions */}
        <div className="flex justify-between items-center">
          <p className="text-muted-foreground">
            {roles.length} roles configured
          </p>
          <Button onClick={openCreateDialog} data-testid="create-role-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Role
          </Button>
        </div>

        {/* Roles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading ? (
            [...Array(6)].map((_, i) => (
              <Card key={i} className="skeleton-shimmer h-48" />
            ))
          ) : (
            roles.map((role) => (
              <Card 
                key={role.role_id} 
                className="card-hover"
                data-testid={`role-card-${role.role_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-sm flex items-center justify-center role-badge role-${role.role_id}`}>
                        <Shield className="w-4 h-4" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{role.name}</CardTitle>
                        <CardDescription className="text-xs">
                          Level {role.level}
                        </CardDescription>
                      </div>
                    </div>
                    {!role.can_be_modified && (
                      <Lock className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Permissions</p>
                      <div className="flex flex-wrap gap-1">
                        {role.permissions?.includes('all') ? (
                          <Badge variant="default" className="text-xs">All Permissions</Badge>
                        ) : role.permissions?.slice(0, 3).map((perm) => (
                          <Badge key={perm} variant="secondary" className="text-xs">
                            {perm}
                          </Badge>
                        ))}
                        {role.permissions?.length > 3 && !role.permissions?.includes('all') && (
                          <Badge variant="outline" className="text-xs">
                            +{role.permissions.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {role.can_be_modified && (
                      <div className="flex gap-2 pt-2 border-t border-border">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="flex-1"
                          onClick={() => openEditDialog(role)}
                          data-testid={`edit-role-${role.role_id}`}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(role.role_id)}
                          data-testid={`delete-role-${role.role_id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
            <DialogDescription>
              {editingRole ? 'Update role settings and permissions' : 'Create a new role with specific permissions'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role-name">Role Name</Label>
                <Input
                  id="role-name"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  placeholder="e.g. Fleet Manager"
                  data-testid="role-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role-level">Level (1-100)</Label>
                <Input
                  id="role-level"
                  type="number"
                  min="1"
                  max="100"
                  value={roleLevel}
                  onChange={(e) => setRoleLevel(parseInt(e.target.value) || 10)}
                  data-testid="role-level-input"
                />
              </div>
            </div>

            <div className="space-y-4">
              <Label>Permissions</Label>
              {Object.entries(groupedPermissions).map(([category, permissions]) => (
                <div key={category} className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">{category}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {permissions.map((perm) => (
                      <div key={perm.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={perm.id}
                          checked={selectedPermissions.includes(perm.id)}
                          onCheckedChange={() => togglePermission(perm.id)}
                          data-testid={`perm-${perm.id}`}
                        />
                        <Label htmlFor={perm.id} className="text-sm font-normal">
                          {perm.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-role-btn">
              {editingRole ? 'Save Changes' : 'Create Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
